/**
 * Calcoli di prezzo della sezione Competitor, condivisi fra tabella e dialog.
 *
 * Stesse regole del backend (features/competitor/pricing.js):
 * - suggerito = min competitor - 0,01, se diverso dal nostro prezzo;
 * - status = beaten se nostro > min, winning se nostro < min, equal altrimenti.
 * Servono lato client per ricalcolare la riga dopo "Applica prezzo".
 */

export const toNumber = value => {
	if (value === undefined || value === null || value === '') {
		return null;
	}
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

export const round2 = value => Math.round(value * 100) / 100;

/** confronto di una cella: 'better' = siamo piu' economici, 'worse' = piu' cari, null = nessun abbinamento */
export const cellStatus = (our, their) => {
	const a = toNumber(our);
	const b = toNumber(their);
	if (a === null || b === null) {
		return null;
	}
	const diff = round2(a - b);
	if (diff < 0) return 'better';
	if (diff > 0) return 'worse';
	return 'equal';
};

/** prezzo piu' basso fra i siti abbinati alla riga */
export const minCompetitor = row => {
	const prezzi = Object.values((row && row.competitors) || {})
		.map(c => toNumber(c && c.price))
		.filter(p => p !== null && p > 0);
	return prezzi.length ? Math.min(...prezzi) : null;
};

/** prezzo proposto: un centesimo sotto il concorrente piu' economico, null se coincide col nostro */
export const suggestedPrice = (price, min) => {
	const m = toNumber(min);
	if (m === null) {
		return null;
	}
	const value = round2(m - 0.01);
	if (value <= 0 || value === round2(toNumber(price) || 0)) {
		return null;
	}
	return value;
};

/** stato della riga rispetto al minimo dei concorrenti */
export const rowStatus = (price, min) => {
	const c = cellStatus(price, min);
	if (c === 'worse') return 'beaten';
	if (c === 'better') return 'winning';
	return 'equal';
};

/** 'down' = abbassare, 'up' = aumentare, null = nessun suggerimento */
export const suggestionDirection = (price, suggested) => {
	const p = toNumber(price);
	const s = toNumber(suggested);
	if (p === null || s === null || round2(p) === round2(s)) {
		return null;
	}
	return s < p ? 'down' : 'up';
};

/** prezzo netto dall'IVA inclusa, come features/brandimport/mapping.js */
export const netPrice = (price, vat) => {
	const p = toNumber(price);
	if (p === null) {
		return null;
	}
	const v = toNumber(vat) || 0;
	return round2(p / (1 + v / 100));
};

/** riga ricalcolata dopo un nuovo prezzo nostro */
export const withNewPrice = (row, price) => {
	const min = row.minCompetitor !== undefined && row.minCompetitor !== null ? row.minCompetitor : minCompetitor(row);
	return {
		...row,
		price,
		suggested: suggestedPrice(price, min),
		status: rowStatus(price, min),
	};
};

export const money = (value, currency = 'EUR') => {
	const n = toNumber(value);
	if (n === null) {
		return '—';
	}
	return currency === 'EUR' || !currency ? `${n.toFixed(2).replace('.', ',')} €` : `${n.toFixed(2)} ${currency}`;
};
